import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import Logo from "./Logo";
import { API, BACKEND, PRODUCTS, HERO_IMAGE } from "@/config/site";

const inputCls =
  "w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#E60000] outline-none px-4 py-3 font-mono text-sm text-white placeholder:text-[#555] transition-colors";

const tid = (s) => s.replace(/\s+/g, "-").toLowerCase();

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function PriceRow({ productId, productName, price, original, onSave }) {
  const [val, setVal] = useState(price);
  return (
    <div className="border border-[#262626] bg-[#121212] p-4" data-testid={`price-row-${productId}`}>
      <p className="font-mono text-[10px] tracking-[0.2em] text-[#888] uppercase">{productName}</p>
      <p className="font-display text-xl mt-1">
        <span className="text-[#555] line-through text-sm mr-2">${original}</span>${price}
      </p>
      <div className="flex gap-2 mt-3 items-center">
        <span className="font-mono text-sm text-[#888]">$</span>
        <input
          type="number"
          min={0}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          aria-label={`Deal price for ${productName}`}
          data-testid={`price-input-${productId}`}
          className="w-24 bg-[#0A0A0A] border border-[#262626] px-2 py-2 font-mono text-sm text-white outline-none focus:border-[#E60000] transition-colors"
        />
        <button
          onClick={() => onSave(productId, val)}
          data-testid={`price-save-${productId}`}
          className="bg-white text-black font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-2 hover:bg-[#E60000] hover:text-white transition-colors"
        >
          Save
        </button>
      </div>
    </div>
  );
}

function ReviewEditor({ reviews, onSave }) {
  const [list, setList] = useState(reviews);
  const set = (i, k, v) => setList((l) => l.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {list.map((r, i) => (
          <div
            key={i}
            className="border border-[#262626] bg-[#121212] p-4 space-y-2"
            data-testid={`review-edit-${i}`}
          >
            <input
              value={r.name}
              onChange={(e) => set(i, "name", e.target.value)}
              placeholder="CUSTOMER NAME"
              aria-label={`Review ${i + 1} customer name`}
              data-testid={`review-name-${i}`}
              className={inputCls}
            />
            <input
              value={r.product}
              onChange={(e) => set(i, "product", e.target.value)}
              placeholder="PRODUCT THEY BOUGHT"
              aria-label={`Review ${i + 1} product`}
              data-testid={`review-product-${i}`}
              className={inputCls}
            />
            <textarea
              value={r.text}
              onChange={(e) => set(i, "text", e.target.value)}
              placeholder="THEIR QUOTE"
              aria-label={`Review ${i + 1} quote`}
              data-testid={`review-text-${i}`}
              className={`${inputCls} min-h-[80px] resize-none`}
            />
          </div>
        ))}
      </div>
      <button
        onClick={() => onSave(list)}
        data-testid="save-reviews-btn"
        className="mt-4 bg-[#E60000] text-white font-mono text-[10px] tracking-[0.15em] uppercase px-5 py-3 hover:bg-white hover:text-black transition-colors"
      >
        Save reviews
      </button>
    </div>
  );
}

function ImageSlot({ slot, label, current, onUpload }) {  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    await onUpload(slot, file);
    setFile(null);
    setBusy(false);
  };
  return (
    <div className="border border-[#262626] bg-[#121212] p-4" data-testid={`image-slot-${slot}`}>
      <p className="font-mono text-[10px] tracking-[0.2em] text-[#888] uppercase">{label}</p>
      <div className="mt-3 border border-[#1a1a1a] bg-[#0A0A0A] h-36 overflow-hidden">
        {current && <img src={current} alt={label} className="w-full h-full object-cover" />}
      </div>
      <div className="flex gap-2 mt-3 items-center">
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setFile(e.target.files[0] || null)}
          data-testid={`image-input-${slot}`}
          className="flex-1 min-w-0 font-mono text-[10px] text-[#888] file:bg-white file:text-black file:border-0 file:px-3 file:py-2 file:font-mono file:text-[10px] file:uppercase file:mr-2 file:cursor-pointer"
        />
        <button
          onClick={upload}
          disabled={!file || busy}
          data-testid={`image-upload-${slot}`}
          className="bg-[#E60000] text-white font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-2 hover:bg-white hover:text-black transition-colors disabled:opacity-40"
        >
          {busy ? "..." : "Upload"}
        </button>
      </div>
    </div>
  );
}

function StockRow({ productId, productName, pack, onSave }) {  const [val, setVal] = useState(pack.max);
  return (
    <div
      className="border border-[#262626] bg-[#121212] p-4"
      data-testid={`stock-row-${productId}-${tid(pack.label)}`}
    >
      <p className="font-mono text-[10px] tracking-[0.2em] text-[#888] uppercase">{productName}</p>
      <p className="font-display text-xl mt-1 uppercase">{pack.label}</p>
      <p className="font-mono text-[10px] text-[#E60000] mt-1 uppercase tracking-[0.15em]">
        {pack.remaining} of {pack.max} left
      </p>
      <div className="flex gap-2 mt-3">
        <input
          type="number"
          min={0}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          aria-label={`Total ${pack.label} stock for ${productName}`}
          data-testid={`stock-input-${productId}-${tid(pack.label)}`}
          className="w-20 bg-[#0A0A0A] border border-[#262626] px-2 py-2 font-mono text-sm text-white outline-none focus:border-[#E60000] transition-colors"
        />
        <button
          onClick={() => onSave(productId, pack.label, val)}
          data-testid={`stock-save-${productId}-${tid(pack.label)}`}
          className="bg-white text-black font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-2 hover:bg-[#E60000] hover:text-white transition-colors"
        >
          Save
        </button>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [admin, setAdmin] = useState(undefined);
  const [orders, setOrders] = useState([]);
  const [stock, setStock] = useState([]);
  const [images, setImages] = useState({});
  const [reviews, setReviews] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { withCredentials: true });
      setOrders(data);
      const s = await axios.get(`${API}/stock`);
      setStock(s.data);
      const img = await axios.get(`${API}/images`);
      setImages(img.data);
      const rv = await axios.get(`${API}/reviews`);
      setReviews(rv.data);
    } catch {
      setAdmin(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(() => {
        setAdmin(true);
        loadOrders();
      })
      .catch(() => setAdmin(false));
  }, [loadOrders]);

  const login = async () => {
    setBusy(true);
    setError("");
    try {
      await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setAdmin(true);
      loadOrders();
      toast.success("Welcome back, boss.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setAdmin(false);
    setOrders([]);
  };

  const markShipped = async (orderId) => {
    try {
      const { data } = await axios.post(`${API}/admin/orders/${orderId}/shipped`, {}, { withCredentials: true });
      toast.success(data.shipped_email_sent ? "Marked as shipped — customer emailed" : "Marked as shipped (email failed)");
      loadOrders();
    } catch {
      toast.error("Could not update that order");
    }
  };

  const saveStock = async (productId, packLabel, value) => {
    try {
      await axios.post(
        `${API}/admin/stock`,
        { product_id: productId, pack_label: packLabel, max: Number(value) },
        { withCredentials: true }
      );
      toast.success("Stock updated");
      loadOrders();
    } catch {
      toast.error("Could not update stock");
    }
  };

  const savePrice = async (productId, value) => {
    try {
      await axios.post(
        `${API}/admin/pricing`,
        { product_id: productId, deal_price: Number(value) },
        { withCredentials: true }
      );
      toast.success("Price updated");
      loadOrders();
    } catch {
      toast.error("Could not update price");
    }
  };

  const uploadImage = async (slot, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/images/${slot}`, fd, { withCredentials: true });
      toast.success("Image updated — live on the storefront");
      loadOrders();
    } catch {
      toast.error("Upload failed — use an image under 8MB");
    }
  };

  const saveReviews = async (list) => {
    try {
      await axios.post(`${API}/admin/reviews`, { reviews: list }, { withCredentials: true });
      toast.success("Reviews updated — live on the storefront");
      loadOrders();
    } catch {
      toast.error("Could not save reviews");
    }
  };

  const imageFor = (slot) => {
    if (images[slot]) return images[slot].startsWith("/") ? `${BACKEND}${images[slot]}` : images[slot];
    if (slot === "hero") return HERO_IMAGE;
    return PRODUCTS.find((p) => p.id === slot)?.image;
  };

  const toShip = orders.filter((o) => o.status === "verified");
  const shipped = orders.filter((o) => o.status === "shipped");
  const paid = orders.filter((o) => o.status === "verified" || o.status === "shipped");
  const revenue = paid.reduce((a, o) => a + o.total, 0);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="flex items-center justify-between px-5 sm:px-10 py-6 border-b border-[#1a1a1a]">
        <Logo />
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] sm:text-xs tracking-[0.3em] text-[#E60000] border border-[#E60000]/40 px-3 py-2">
            OWNER ONLY
          </span>
          {admin && (
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              aria-label="Log out"
              className="text-[#888] hover:text-[#E60000] transition-colors"
            >
              <LogOut size={18} />
            </button>
          )}
        </div>
      </nav>

      {admin === undefined && (
        <p className="font-mono text-xs text-[#888] text-center py-24 tracking-[0.3em]">
          CHECKING SESSION...
        </p>
      )}

      {admin === false && (
        <div className="max-w-sm mx-auto px-5 py-24">
          <h1 className="font-display uppercase text-4xl" data-testid="admin-login-title">
            Owner login
          </h1>
          <p className="font-mono text-xs text-[#888] mt-2">
            This page is private. Orders live behind this door.
          </p>
          <div className="mt-8 space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              className={inputCls}
              placeholder="EMAIL"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              data-testid="admin-password-input"
              type="password"
              className={inputCls}
              placeholder="PASSWORD"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && login()}
            />
            {error && (
              <p data-testid="admin-login-error" className="font-mono text-xs text-[#E60000]">
                {error}
              </p>
            )}
            <button
              onClick={login}
              disabled={busy}
              data-testid="admin-login-btn"
              className="w-full bg-[#E60000] text-white font-display uppercase tracking-wide text-lg py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
            >
              {busy ? "Opening..." : "Open dashboard"}
            </button>
          </div>
        </div>
      )}

      {admin === true && (
        <main className="max-w-7xl mx-auto px-5 sm:px-10 py-10" data-testid="admin-dashboard">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="font-mono text-xs tracking-[0.35em] text-[#E60000] uppercase">
                4TellThem Socks
              </p>
              <h1 className="font-display uppercase text-4xl sm:text-5xl mt-2">Orders</h1>
            </div>
            <button
              onClick={loadOrders}
              data-testid="refresh-orders-btn"
              className="inline-flex items-center gap-2 border border-[#333] px-4 py-3 font-mono text-xs tracking-[0.2em] uppercase hover:border-[#E60000] hover:text-[#E60000] transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
            <div className="border border-[#262626] bg-[#121212] p-4">
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#888] uppercase">Orders</p>
              <p className="font-display text-3xl mt-1" data-testid="stat-total-orders">
                {orders.length}
              </p>
            </div>
            <div className="border border-[#E60000]/50 bg-[#121212] p-4">
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#E60000] uppercase">
                Left to ship
              </p>
              <p className="font-display text-3xl mt-1 text-[#E60000]" data-testid="stat-to-ship">
                {toShip.length}
              </p>
            </div>
            <div className="border border-[#262626] bg-[#121212] p-4">
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#888] uppercase">Shipped</p>
              <p className="font-display text-3xl mt-1" data-testid="stat-shipped">
                {shipped.length}
              </p>
            </div>
            <div className="border border-[#262626] bg-[#121212] p-4">
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#888] uppercase">
                Paid revenue
              </p>
              <p className="font-display text-3xl mt-1" data-testid="stat-revenue">
                ${revenue}
              </p>
            </div>
          </div>

          <div className="mt-8 overflow-x-auto border border-[#262626]">
            <table className="w-full text-left font-mono text-xs" data-testid="orders-table">
              <thead>
                <tr className="border-b border-[#262626] text-[#666] uppercase tracking-[0.15em]">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Pack</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Ship to</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-[#555]" data-testid="orders-empty">
                      No orders yet. Share the drop.
                    </td>
                  </tr>
                )}
                {orders.map((o) => (
                  <tr
                    key={o.order_id}
                    data-testid={`order-row-${o.order_id}`}
                    className="border-b border-[#1a1a1a] hover:bg-[#121212] transition-colors"
                  >
                    <td className="px-4 py-3 text-[#888] whitespace-nowrap">
                      {new Date(o.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white">{o.name}</div>
                      <div className="text-[#666]">{o.email}</div>
                    </td>
                    <td className="px-4 py-3 text-white">{o.product_name}</td>
                    <td className="px-4 py-3 text-[#999] whitespace-nowrap">
                      {o.quantity} x {o.pack_label} ({o.total_pairs} pairs)
                    </td>
                    <td className="px-4 py-3 text-white">${o.total}</td>
                    <td className="px-4 py-3 uppercase text-[#999]">{o.payment_method}</td>
                    <td className="px-4 py-3">
                      {o.status === "shipped" ? (
                        <span
                          data-testid={`order-status-${o.order_id}`}
                          className="px-2 py-1 text-[10px] tracking-[0.15em] uppercase bg-white text-black"
                        >
                          Shipped
                        </span>
                      ) : o.status === "verified" ? (
                        <button
                          onClick={() => markShipped(o.order_id)}
                          data-testid={`ship-btn-${o.order_id}`}
                          className="px-2 py-1 text-[10px] tracking-[0.15em] uppercase bg-[#E60000] text-white hover:bg-white hover:text-black transition-colors"
                        >
                          Mark shipped
                        </button>
                      ) : (
                        <span
                          data-testid={`order-status-${o.order_id}`}
                          className="px-2 py-1 text-[10px] tracking-[0.15em] uppercase border border-[#333] text-[#888]"
                        >
                          Awaiting
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#666] max-w-[200px] truncate">
                      {o.address}, {o.city}, {o.state} {o.zip}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="font-mono text-[10px] text-[#555] mt-4 tracking-[0.1em]">
            Verified = customer entered their email code. Match their payment to their order email
            before shipping.
          </p>

          <div className="mt-14" data-testid="pricing-section">
            <p className="font-mono text-xs tracking-[0.35em] text-[#E60000] uppercase">
              Price editor
            </p>
            <h2 className="font-display uppercase text-3xl sm:text-4xl mt-2">Deal prices</h2>
            <p className="font-mono text-[11px] text-[#666] mt-2 max-w-lg leading-relaxed">
              Change the deal price anytime — when the drop ends, after restocks, whenever. The
              storefront and checkout update instantly. Original price stays $25.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {stock.map((s) => (
                <PriceRow
                  key={`${s.product_id}-${s.deal_price}`}
                  productId={s.product_id}
                  productName={PRODUCTS.find((x) => x.id === s.product_id)?.name || s.product_id}
                  price={s.deal_price}
                  original={s.original_price}
                  onSave={savePrice}
                />
              ))}
            </div>
          </div>

          <div className="mt-14" data-testid="images-section">
            <p className="font-mono text-xs tracking-[0.35em] text-[#E60000] uppercase">
              Site images
            </p>
            <h2 className="font-display uppercase text-3xl sm:text-4xl mt-2">Customize photos</h2>
            <p className="font-mono text-[11px] text-[#666] mt-2 max-w-lg leading-relaxed">
              Upload your real Supreme sock photos — they replace the stock images on the
              storefront instantly.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ImageSlot slot="hero" label="Hero photo" current={imageFor("hero")} onUpload={uploadImage} />
              <ImageSlot
                slot="white-supreme-socks"
                label="White socks photo"
                current={imageFor("white-supreme-socks")}
                onUpload={uploadImage}
              />
              <ImageSlot
                slot="black-supreme-socks"
                label="Black socks photo"
                current={imageFor("black-supreme-socks")}
                onUpload={uploadImage}
              />
            </div>
          </div>

          <div className="mt-14" data-testid="reviews-editor-section">
            <p className="font-mono text-xs tracking-[0.35em] text-[#E60000] uppercase">
              Reviews
            </p>
            <h2 className="font-display uppercase text-3xl sm:text-4xl mt-2">Edit reviews</h2>
            <p className="font-mono text-[11px] text-[#666] mt-2 max-w-lg leading-relaxed">
              Change the names, products and quotes — the storefront reviews section updates
              instantly.
            </p>
            <div className="mt-6">
              {reviews.length > 0 && (
                <ReviewEditor
                  key={JSON.stringify(reviews)}
                  reviews={reviews}
                  onSave={saveReviews}
                />
              )}
            </div>
          </div>

          <div className="mt-14" data-testid="inventory-section">
            <p className="font-mono text-xs tracking-[0.35em] text-[#E60000] uppercase">
              Restock control
            </p>
            <h2 className="font-display uppercase text-3xl sm:text-4xl mt-2">Inventory</h2>
            <p className="font-mono text-[11px] text-[#666] mt-2 max-w-lg leading-relaxed">
              Set the total packs when new inventory lands. Packs already sold stay counted — the
              storefront updates instantly.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {stock.map((s) =>
                s.packs.map((p) => (
                  <StockRow
                    key={`${s.product_id}-${p.label}-${p.max}`}
                    productId={s.product_id}
                    productName={PRODUCTS.find((x) => x.id === s.product_id)?.name || s.product_id}
                    pack={p}
                    onSave={saveStock}
                  />
                ))
              )}
            </div>
          </div>
        </main>
      )}
    </div>
  );
}
